//
// Formatted Table
//
// preventScrollUp: when set, forces table to be scrolled down at least one pixel
//                  (this way, when data gets pre-pended, tables doesn't scroll up)
//
// Hierarchical data can also be represented, and parts of the table can be folded (hidden)
//  based on this hierarchy.
// In that mode:
//
//  * By default, each data row's hierarchyIdField field contains that row's "children"
//    If hierarchyIdField='children', data is structured as:
//      data= [
//        {
//          id: 'data0',
//          children: [
//            {
//              ... more data
//            }
//          ]
//        }
//      ]
//
//  * If hierarchyFlattened is set, the data has already been flattened, hierarchyIdField
//    only indicates the row's hierarchy depth
//    If hierarchyIdField='depth', data is structured as:
//      data= [
//        {
//          id: 'data0',
//          depth: 0,
//        },
//        {
//          // Will be displayed as a child to the line above
//          id: 'data1',
//          depth: 1,
//        }
//      ]
//
//
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import {
  displayJson,
  emptyArray,
  // logDev,
  logError,
} from '../utils';

import CircularProgress from './CircularProgress';

// Scroll positions are compared with a 1px slack: browsers report
// fractional scrollTop/scrollHeight (sub-pixel layout, scrollbar-gutter,
// zoom), so an exact equality at the bottom edge is almost never true.
const EDGE_TOLERANCE_PX = 1;

const initialState = {
  // An identifier, only used to identify when the table is a new
  // one entirely
  label: undefined,
  // Current scroll position, at the very edge (within EDGE_TOLERANCE_PX)
  atTop: false,
  atBottom: false,
  // Same edges widened by the caller's prefetch offset — drives the
  // onScrollTo* callbacks so they can fire before the last row is reached
  nearTop: false,
  nearBottom: false,
  // Used to detect transition
  prevAtTop: false,
  prevAtBottom: false,
  prevNearTop: false,
  prevNearBottom: false,
  // Used to prevent scroll at top and bottom:
  topId: undefined,
  bottomId: undefined,
  scrollToId: undefined,
  scrollAlign: undefined,
  // In case a hierarchy is used, flattened data:
  // (a 'th' key is added to each entry, holding hierarchy info)
  hierarchyData: undefined,
  // keep track of closed lines (Set for O(1) lookups)
  hierarchyClosed: new Set(),
  // and what is currently hidden (Set for O(1) lookups)
  hierarchyHidden: new Set(),
  // level at which hierarchy is hidden by default
  hierarchyCloseLevel: undefined,
  // ignore collapseAll event when trigger doesn't really change
  previousCollapseAllTrigger: undefined,
  ready: false,
  // Track which hierarchy button is being processed (prevents rapid clicks)
  processingHierarchyId: undefined,
};

const flattenData = (data, hierarchyIdField, hierarchyFlattened, level = 0) => {
  if (!data) return data;

  const ret = [];
  const lastIndex = data.length - 1;

  if (hierarchyFlattened) {
    // Data is already flattened, we only need to add some fields for display purposes
    //
    let newElement;
    let sub = [];

    data.forEach((element, index) => {
      if (element[hierarchyIdField] > level) {
        // Set aside all the children
        sub.push(element);
      } else {
        // A new element at this level
        if (newElement) {
          // There is a previous one to finalize (after we've gone through all its children)
          // number of children at level just below
          newElement.th.children = sub.length;
          // set for last entry at this level
          newElement.th.end = false;
          ret.push(newElement);
        }
        if (sub.length) {
          // We have some children from the previous element at the same level to flatten,
          // do that first
          const subFlattened = flattenData(sub, hierarchyIdField, hierarchyFlattened, level + 1);

          subFlattened.forEach((subElement) => {
            ret.push(subElement);
          });
          sub = [];
        }

        newElement = {
          ...element,
          th: {
            level,
            // set for first entry at this level
            start: index === 0,
          },
        };
      }
    });

    // Finalize last element
    if (newElement) {
      // There is a previous one to finalize (after we've gone through all its children)
      // number of children at level just below
      newElement.th.children = sub.length;
      // set for last entry at this level
      newElement.th.end = false;
      ret.push(newElement);
    }
    if (sub.length) {
      // We have some children from the previous element at the same level to flatten,
      // do that first
      const subFlattened = flattenData(sub, hierarchyIdField, hierarchyFlattened, level + 1);

      subFlattened.forEach((subElement) => {
        ret.push(subElement);
      });
      sub = [];
    }

    return ret;
  }

  data.forEach((element, index) => {
    const newElement = { ...element };

    const sub = element[hierarchyIdField];

    delete newElement[hierarchyIdField];

    newElement.th = {
      level,
      // set for first entry at this level
      start: index === 0,
      // number of children at level just below
      children: (sub) ? sub.length : 0,
      // set for last entry at this level
      end: index === lastIndex,

    };

    ret.push(newElement);

    if (sub && sub.length) {
      const subFlattened = flattenData(sub, hierarchyIdField, hierarchyFlattened, level + 1);

      subFlattened.forEach((subElement) => {
        ret.push(subElement);
      });
    }
  });

  return ret;
};

const listHidden = (
  hierarchyData,
  rowIdField,
  hierarchyClosed,
) => {
  const ret = new Set();
  let closedLevel;

  hierarchyData.forEach((rowData) => {
    // console.log(`rowIdField=${rowIdField} rowData=${JSON.stringify(rowData)}`);
    const id = rowData[rowIdField];
    const idString = id.toString();
    const { level } = rowData.th;
    // console.log(`id=${idString} level=${level}`);

    if ((closedLevel !== undefined) && (level > closedLevel)) {
      // We're below a parent node which is closed > hide this node
      // console.log(`Hide child id=${idString}`);
      ret.add(idString);
      return;
    }
    // console.log(`Show child id=${idString}`);

    if (hierarchyClosed.has(idString)) {
      // We just found a visible but closed node
      // > nodes below must be hidden
      // console.log(`Close id=${idString}`);
      closedLevel = level;
      return;
    }

    // We're back at the same level as the previously closed node, or above
    closedLevel = undefined;
  });

  return ret;
};

//
// Mark outlined elements, also indicate in parents how many children are outlined
const outlineElements = (hierarchyData, rowIdField, outlineId) => {
  if (!(hierarchyData && outlineId)) return hierarchyData;

  const ret = [];
  const outlinedAtLevel = [];

  let index = hierarchyData.length - 1;

  while (index >= 0) {
    const rowData = hierarchyData[index];
    const id = rowData[rowIdField]; // .toString();
    const { level } = rowData.th;

    if (outlineId.includes(id)) {
      // Mark this item as outlined
      rowData.th.outlined = true;

      // Increment children count for parents
      let countDown = level - 1;
      while (countDown >= 0) {
        outlinedAtLevel[countDown] = (outlinedAtLevel[countDown] === undefined)
          ? 1
          : outlinedAtLevel[countDown] + 1;

        countDown -= 1;
      }
    } else {
      rowData.th.outlined = false;
    }

    // Record children found below
    if (outlinedAtLevel[level] !== undefined) {
      rowData.th.outlinedChildren = outlinedAtLevel[level];
    } else {
      rowData.th.outlinedChildren = undefined;
    }

    // Clear children count for next folders at same level
    outlinedAtLevel.splice(level);

    index -= 1;
    ret.unshift(rowData);
  }

  // const logDisplay = ret
  //   .filter((thisRow) => (thisRow?.th?.outlined || thisRow?.th?.outlinedChildren))
  //   .map((thisRow) => (`${thisRow[rowIdField]}: outlined=${
  //     thisRow.th.outlined} outlinedChildren=${thisRow.th.outlinedChildren}`));
  // console.log(`outlineElements outlineId=${
  //   displayJson(outlineId)} ret=${displayJson(logDisplay)}`);

  return ret;
};

//
// Create a list of closed line, so that outlined rows are visible
const closeNonOutlinedElements = (hierarchyData, rowIdField) => {
  if (!hierarchyData) return new Set();

  const ret = new Set();
  let openLevel;
  let index = hierarchyData.length - 1;

  while (index >= 0) {
    const rowData = hierarchyData[index];
    const id = rowData[rowIdField]; // .toString();
    const idString = id.toString();
    const { level, outlined } = rowData.th;

    // See if a child has asked to open this level
    if (openLevel === level) {
      openLevel = level - 1;
    } else if ((openLevel === undefined) || (level > openLevel)) {
      // element below the level to open > close it
      ret.add(idString);
    }

    // If this item is outlined, open all parents
    if (outlined) {
      openLevel = level - 1;
    }

    index -= 1;
  }

  // console.log(`closeNonOutlinedElements ret=${displayJson(ret)}`);

  return ret;
};

const markSelectedElements = (hierarchyData, rowIdField, selectedIds) => {
  if (!(hierarchyData && selectedIds?.length)) return hierarchyData;

  const ret = [];
  const selectedAtLevel = [];

  let index = hierarchyData.length - 1;

  while (index >= 0) {
    const rowData = hierarchyData[index];
    const id = rowData[rowIdField];
    const { level } = rowData.th;

    if (selectedIds.includes(id)) {
      // Mark this item as selected for hierarchy purposes
      rowData.th.hierarchySelected = true;

      // Increment children count for parents
      let countDown = level - 1;
      while (countDown >= 0) {
        selectedAtLevel[countDown] = (selectedAtLevel[countDown] === undefined)
          ? 1
          : selectedAtLevel[countDown] + 1;

        countDown -= 1;
      }
    } else {
      rowData.th.hierarchySelected = false;
    }

    // Record selected children found below
    if (selectedAtLevel[level] !== undefined) {
      rowData.th.selectedChildren = selectedAtLevel[level];
    } else {
      rowData.th.selectedChildren = undefined;
    }

    // Clear children count for next folders at same level
    selectedAtLevel.splice(level);

    index -= 1;
    ret.unshift(rowData);
  }

  return ret;
};

const closeNonSelectedElements = (hierarchyData, rowIdField) => {
  if (!hierarchyData) return new Set();

  const ret = new Set();
  let openLevel;
  let index = hierarchyData.length - 1;

  while (index >= 0) {
    const rowData = hierarchyData[index];
    const id = rowData[rowIdField];
    const idString = id.toString();
    const { level, hierarchySelected } = rowData.th;

    // See if a child has asked to open this level
    if (openLevel === level) {
      openLevel = level - 1;
    } else if ((openLevel === undefined) || (level > openLevel)) {
      // element below the level to open > close it
      ret.add(idString);
    }

    // If this item is selected, open all parents
    if (hierarchySelected) {
      openLevel = level - 1;
    }

    index -= 1;
  }

  return ret;
};

// The rows on the way down to a selected row, by id string, once markSelectedElements ran: an
// initial close level leaves them open so the selection shows
const ancestorsOfSelected = (hierarchyData, rowIdField) => {
  const ret = new Set();
  let openLevel;
  let index = hierarchyData.length - 1;

  while (index >= 0) {
    const rowData = hierarchyData[index];
    const { level, hierarchySelected } = rowData.th;

    if (openLevel === level) {
      ret.add(rowData[rowIdField].toString());
      openLevel = level - 1;
    }
    if (hierarchySelected) openLevel = level - 1;

    index -= 1;
  }

  return ret;
};

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'scroll': {
      const distanceTop = action.scrollTop;
      const distanceBottom = action.scrollHeight - action.clientHeight - action.scrollTop;
      return ({
        ...state,
        atTop: (distanceTop <= EDGE_TOLERANCE_PX),
        atBottom: (distanceBottom <= EDGE_TOLERANCE_PX),
        nearTop: (distanceTop <= action.edgeOffsetPx),
        nearBottom: (distanceBottom <= action.edgeOffsetPx),
        scrollToId: undefined,
      });
    }

    case 'dataChanged': {
      const newTopId = (action.data && action.data[0])
        ? `cell_${action.rowIdField}_${action.data[0][action.rowIdField]}`
        : undefined;
      const newBottomId = (action.data?.length)
        ? `cell_${action.rowIdField}_${action.data[action.data.length - 1][action.rowIdField]}`
        : undefined;

      let newScrollToId;
      let newScrollAlign;
      if (state.atTop && action.preventScrollOnPrepend && (state.topId !== newTopId)) {
        newScrollAlign = 'start';
        newScrollToId = state.topId;
      } else if (state.atBottom && (state.bottomId !== newBottomId)) {
        newScrollAlign = 'end';
        if (action.preventScrollOnAppend) {
          // Stay on the same bottom line display
          newScrollToId = state.bottomId;
        } else {
          // Scroll to the last line
          newScrollToId = newBottomId;
        }
      }

      let newHierarchyData;
      if (action.hierarchyIdField) {
        newHierarchyData = flattenData(
          action.data,
          action.hierarchyIdField,
          action.hierarchyFlattened,
          0,
        );
      }

      let newHierarchyClosed;
      let newHierarchyHidden;
      if (state.hierarchyData === undefined
          && action.openOnlySelectedIdsOnInit
          && action.selectedId
          && (Array.isArray(action.selectedId) ? action.selectedId.length : true)
          && newHierarchyData) {
        // This is the first load and we want to open only paths to selected items
        const selectedArray = Array.isArray(action.selectedId)
          ? action.selectedId
          : [action.selectedId];

        // Mark selected items in hierarchy
        newHierarchyData = markSelectedElements(
          newHierarchyData,
          action.rowIdField,
          selectedArray,
        );

        // Close everything except paths to selected items
        newHierarchyClosed = closeNonSelectedElements(
          newHierarchyData,
          action.rowIdField,
        );

        newHierarchyHidden = listHidden(
          newHierarchyData,
          action.rowIdField,
          newHierarchyClosed,
        );
      } else if ((action.label !== state.label)
          || (action.hierarchyCloseLevel !== state.hierarchyCloseLevel)
          // Data arriving after an empty first load gets the initial close level too
          || ((state.hierarchyData?.length === 0) && (newHierarchyData?.length > 0))) {
        // console.log(`Table label=${action.label}`);
        // console.log(`Table hierarchyIdField=${action.hierarchyIdField}`);
        // console.log(`Table hierarchyCloseLevel=${action.hierarchyCloseLevel}`);
        if (action.hierarchyIdField && (action.hierarchyCloseLevel !== undefined)) {
          // console.log(`Compute with newHierarchyData=${displayJson(newHierarchyData)}`);
          const selectedArray = (Array.isArray(action.selectedId) ? action.selectedId : [action.selectedId])
            .filter((id) => (id !== undefined) && (id !== null));
          newHierarchyData = markSelectedElements(newHierarchyData, action.rowIdField, selectedArray);
          const keptOpen = ancestorsOfSelected(newHierarchyData, action.rowIdField);
          newHierarchyClosed = new Set();
          newHierarchyData.forEach((thisEl) => {
            if ((thisEl.th.level >= action.hierarchyCloseLevel) && (!keptOpen.has(thisEl[action.rowIdField]?.toString()))) {
              if (!thisEl[action.rowIdField]) {
                logError(
                  `Table newHierarchyData[${action.rowIdField}] undefined, thisEl=${
                    displayJson(thisEl)}`,
                  'Table.reducer.dataChanged.undefinedRowId',
                  {
                    rowIdField: action?.rowIdField,
                    elKeys: thisEl && Object.keys(thisEl),
                    label: action?.label,
                  },
                );
              }
              newHierarchyClosed.add(thisEl[action.rowIdField].toString());
            }
          });
          newHierarchyHidden = listHidden(
            newHierarchyData,
            action.rowIdField,
            newHierarchyClosed,
          );
        }
        // console.log(`Table newHierarchyClosed=${displayJson(newHierarchyClosed)}`);
        // console.log(`Table newHierarchyHidden=${displayJson(newHierarchyHidden)}`);
      }

      // console.log('Data changed, compute done');
      return ({
        ...state,
        topId: newTopId,
        bottomId: newBottomId,
        scrollToId: newScrollToId,
        scrollAlign: newScrollAlign,
        hierarchyData: newHierarchyData,
        hierarchyCloseLevel: action.hierarchyCloseLevel,
        hierarchyClosed: newHierarchyClosed || state.hierarchyClosed,
        hierarchyHidden: newHierarchyHidden || state.hierarchyHidden,
        label: action.label,
        ready: true,
      });
    }

    case 'outlineId': {
      const newHierarchyData = outlineElements(
        state.hierarchyData,
        action.rowIdField,
        action.outlineId,
      );

      let newHierarchyClosed;
      let newHierarchyHidden;
      if (action.openOnlyOutlinedIds) {
        newHierarchyClosed = closeNonOutlinedElements(
          newHierarchyData,
          action.rowIdField,
        );

        newHierarchyHidden = listHidden(
          newHierarchyData,
          action.rowIdField,
          newHierarchyClosed,
        );
      }

      return ({
        ...state,
        hierarchyData: newHierarchyData,
        hierarchyClosed: newHierarchyClosed || state.hierarchyClosed,
        hierarchyHidden: newHierarchyHidden || state.hierarchyHidden,
        ready: true,
      });
    }

    case 'notReady':
      return ({
        ...state,
        ready: false,
      });

    case 'scrollDone':
      return ({
        ...state,
        scrollToId: undefined,
      });

    case 'latchAtTop':
      return ({
        ...state,
        prevAtTop: action.atTop,
      });

    case 'latchAtBottom':
      return ({
        ...state,
        prevAtBottom: action.atBottom,
      });

    case 'latchNearTop':
      return ({
        ...state,
        prevNearTop: action.nearTop,
      });

    case 'latchNearBottom':
      return ({
        ...state,
        prevNearBottom: action.nearBottom,
      });

    case 'setProcessingHierarchy':
      return ({
        ...state,
        processingHierarchyId: action.id,
      });

    case 'clickHierarchyOpenClose': {
      const { id } = action;
      const idString = id.toString();

      // Create new Set (immutable update for React state)
      const newHierarchyClosed = new Set(state.hierarchyClosed);
      if (newHierarchyClosed.has(idString)) {
        // Open up a section
        newHierarchyClosed.delete(idString);
      } else {
        // Closing up a section
        // console.log(`Close ${idString}`);
        newHierarchyClosed.add(idString);
      }

      // console.log(`newHierarchyClosed=${JSON.stringify(newHierarchyClosed)}`);
      const newHierarchyHidden = listHidden(
        state.hierarchyData,
        action.rowIdField,
        newHierarchyClosed,
      );
      // console.log(`newHierarchyHidden=${JSON.stringify(newHierarchyHidden)}`);

      const newState = {
        ...state,
        hierarchyClosed: newHierarchyClosed,
        hierarchyHidden: newHierarchyHidden,
        ready: true,
        processingHierarchyId: undefined,
      };
      // console.log(`Set newState.hierarchyClosed=${displayJson(newState.hierarchyClosed, 2)}`);
      // console.log(`Set newState.hierarchyHidden=${displayJson(newState.hierarchyHidden, 2)}`);

      return (newState);
    }

    case 'collapseAll': {
      if (!state.hierarchyData) {
        // console.log('Nothing to collapse?');
        return state;
      }

      if (state.previousCollapseAllTrigger === undefined
          || (state.previousCollapseAllTrigger === action.collapseAllTrigger)) {
        // Ignore first useEffect, and when collapseAllTrigger did not change
        return ({
          ...state,
          previousCollapseAllTrigger: action.collapseAllTrigger,
        });
      }

      const newHierarchyClosed = new Set();
      state.hierarchyData.forEach((thisEl) => {
        if (thisEl.th.level >= action.hierarchyCloseLevel) {
          if (!thisEl[action.rowIdField]) {
            logError(
              `Table state.hierarchyData[${action.rowIdField}] undefined, thisEl=${
                displayJson(thisEl)}`,
              'Table.reducer.collapseAll.undefinedRowId',
              {
                rowIdField: action?.rowIdField,
                elKeys: thisEl && Object.keys(thisEl),
              },
            );
          }
          newHierarchyClosed.add(thisEl[action.rowIdField].toString());
        }
      });

      const newHierarchyHidden = listHidden(
        state.hierarchyData,
        action.rowIdField,
        newHierarchyClosed,
      );

      return ({
        ...state,
        hierarchyClosed: newHierarchyClosed,
        hierarchyHidden: newHierarchyHidden,
      });
    }

    default:
      logError(
        `Table stateReducer unknown action=${displayJson(action)}`,
        'Table.reducer.unknownAction',
        { actionType: action?.type },
      );
      return state;
  }
};

//
// Memoized row component - skips re-rendering when props haven't changed.
// On hierarchy toggle, only rows whose visibility/closed/processing state
// changed will actually re-render; all others are skipped by React.memo.
//
const TableRow = memo(
  ({
    rowData,
    rowIdField,
    rowIndex,
    columns,
    columnClassNames,
    cellClassName,
    hierarchyColumn,
    handleRowClickInternal,
    handleHierarchyClick,
    isHidden,
    isSelected,
    isClosed,
    isProcessing,
    nullDisplay,
    undefinedDisplay,
    renderFullWidthCell,
    hasOnClickRow,
  }) => {
    // CSS-based hiding: hidden rows stay in the DOM with display:none
    // so toggling only flips a CSS property — no DOM insertion/removal
    if (isHidden) return <div style={{ display: 'none' }} />;

    const thisRowId = rowData[rowIdField] || '';
    const thisRowIdString = thisRowId.toString();

    if (rowData.tableRowFullWidth) {
      // This row spans the whole Table's width
      let thisCellClassName = cellClassName;
      thisCellClassName += ' col-start-1 col-span-full';
      const key = `cellFullW_${thisRowId}`;
      let dataDisplay;
      if (renderFullWidthCell) dataDisplay = renderFullWidthCell(rowData, rowIndex);
      else dataDisplay = '-- renderer undefined --';

      return (
        <div
          className={thisCellClassName}
          key={key}
          id={key}
        >
          {dataDisplay}
        </div>
      );
    }

    if (thisRowId === undefined) {
      logError(
        `Table row id=${thisRowId}, looking for rowIdField=${rowIdField} in thisRowData=${displayJson(rowData)}`,
        'Table.TableRow.undefinedRowId',
        {
          rowIdField,
          rowIndex,
          rowKeys: rowData && Object.keys(rowData),
        },
      );
    }

    let hierarchy = null;
    if (rowData.th) {
      hierarchy = [];

      const thisStyle = (rowData.th.level)
        ? { marginLeft: `${22 * rowData.th.level}px` }
        : undefined;

      if (rowData.th.children) {
        const thisKey = `bta_${thisRowId}`;
        const thisClassName = 'w-5 h-5 shrink-0 mr-1 relative z-10 cursor-pointer';

        if (isClosed) {
          hierarchy.push(
            <button
              key={thisKey}
              type="button"
              id={thisRowId}
              onClick={handleHierarchyClick}
              aria-label="open"
              className={thisClassName}
              style={thisStyle}
              disabled={isProcessing}
            >
              {isProcessing
                ? <CircularProgress sizePx={16} />
                : (
                    <img
                      className="w-full h-full shrink-0"
                      src="/images/png/gui/tableAngleClosed.png"
                      alt="closed folder"
                    />
                  )}
            </button>,
          );
        } else {
          hierarchy.push(
            <button
              key={thisKey}
              type="button"
              id={thisRowId}
              onClick={handleHierarchyClick}
              aria-label="close"
              className={thisClassName}
              style={thisStyle}
              disabled={isProcessing}
            >
              {isProcessing
                ? <CircularProgress sizePx={16} />
                : (
                    <img
                      className="w-full h-full shrink-0"
                      src="/images/png/gui/tableAngleOpened.png"
                      alt="opened folder"
                    />
                  )}
            </button>,
          );
        }
      } else {
        hierarchy.push(
          <img
            className="w-5 shrink-0 mr-1 opacity-40 pointer-events-none relative z-10"
            src="/images/png/gui/tableAngle.png"
            alt="next folder"
            style={thisStyle}
            key={`ta_${thisRowId}`}
          />,
        );
      }
    }

    // display:contents makes this wrapper invisible to the grid layout —
    // child cells participate directly in the parent grid
    return (
      <div style={{ display: 'contents' }}>
        { columns
          .map((thisColumn, columnIndex) => {
            const {
              id: columnId,
              renderRowCell,
            } = thisColumn;
            const cellData = rowData[columnId];

            // Use pre-computed className lookup instead of classNames() call
            const colClasses = columnClassNames[columnIndex];
            const shouldCenter = colClasses.forceCenter
            || ((!colClasses.hasRenderRowCell)
                && ((cellData === null) || (cellData === undefined)));
            let thisCellClassName;
            if (shouldCenter) {
              thisCellClassName = isSelected
                ? colClasses.centerSelected
                : colClasses.centerNotSelected;
            } else {
              thisCellClassName = isSelected
                ? colClasses.startSelected
                : colClasses.startNotSelected;
            }

            const key = `cell_${columnId}_${thisRowId}`;

            let dataDisplay;
            if (renderRowCell) {
              dataDisplay = renderRowCell(
                rowData,
                rowIndex,
                isSelected,
                isClosed,
              );
            } else if (cellData === null) dataDisplay = nullDisplay;
            else if (cellData === undefined) dataDisplay = undefinedDisplay;
            else if (typeof cellData === 'string') dataDisplay = cellData;
            else if (typeof cellData === 'number') dataDisplay = cellData.toString();
            else dataDisplay = JSON.stringify(cellData);

            return (
              <div
                className={thisCellClassName}
                key={key}
                id={key}
              >
                {
                  (hasOnClickRow)
                    ? (
                        <button
                          type="button"
                          className="w-full h-full opacity-0 absolute top-0 left-0"
                          id={`button_${key}`}
                          data-row-id={thisRowIdString}
                          onClick={handleRowClickInternal}
                          aria-label="selectRow"
                        />
                      )
                    : null
                }
                {(columnIndex === hierarchyColumn) ? hierarchy : null}
                {dataDisplay}
              </div>
            );
          })}
      </div>
    );
  },
);
TableRow.displayName = 'TableRow';

TableRow.propTypes = {
  rowData: PropTypes.shape({
    tableRowFullWidth: PropTypes.bool, // set to have this data span the whole row
    th: PropTypes.shape[{
      level: PropTypes.number,
      start: PropTypes.bool,
    }],
  }).isRequired,
  rowIdField: PropTypes.string.isRequired,
  rowIndex: PropTypes.number.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    renderRowCell: PropTypes.func,
  })).isRequired,
  columnClassNames: PropTypes.arrayOf(PropTypes.shape({
    centerSelected: PropTypes.string,
    centerNotSelected: PropTypes.string,
    startSelected: PropTypes.string,
    startNotSelected: PropTypes.string,
    hasRenderRowCell: PropTypes.bool,
    forceCenter: PropTypes.bool,
  })).isRequired,
  cellClassName: PropTypes.string.isRequired,
  hierarchyColumn: PropTypes.number.isRequired,
  handleRowClickInternal: PropTypes.func.isRequired,
  handleHierarchyClick: PropTypes.func.isRequired,
  isHidden: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isClosed: PropTypes.bool,
  isProcessing: PropTypes.bool.isRequired,
  nullDisplay: PropTypes.string.isRequired,
  undefinedDisplay: PropTypes.string.isRequired,
  renderFullWidthCell: PropTypes.func,
  hasOnClickRow: PropTypes.bool.isRequired,
};

function Table(props) {
  const {
    id = 'tableDiv',
    className,
    label,
    columns,
    data: rawData = emptyArray,
    onClickColumn,
    onClickRow,
    rowIdField,
    selectedRowCellClassName = 'font-medium bg-pxPrimary/5',
    selectedId,
    autoScrollToSelected = true,
    openOnlySelectedIdsOnInit = false,
    nullDisplay = '-',
    undefinedDisplay = '?',
    onScrollToTop,
    onScrollToBottom,
    scrollEdgeOffsetPx = 200,
    preventScrollOnPrepend,
    preventScrollOnAppend,
    hierarchyIdField,
    hierarchyFlattened,
    hierarchyCloseLevel,
    renderFullWidthCell,
    cellsClassName,
    showHeader = true,
    showBorders = true,
    hierarchyColumn = 0,
    collapseAllTrigger,
    outlineId = emptyArray,
    openOnlyOutlinedIds,
    busy,
  } = props;
  const data = useDeferredValue(rawData, emptyArray);
  const isDataDeferred = data !== rawData;
  const [state, dispatch] = useReducer(stateReducer, initialState);

  // useEffect(() => { console.log('Table mount'); }, []);

  // useEffect(
  //   () => { console.log(`hierarchyClosed=${displayJson(state.hierarchyClosed)}`); },
  //   [state.hierarchyClosed],
  // );
  // useEffect(
  //   () => { console.log(`elements=${displayJson(state.hierarchyClosed.elements)}`); },
  //   [state.hierarchyClosed.elements],
  // );
  // useEffect(
  //   () => {
  //     console.log(`data[0]=${displayJson(data[0])}`);
  //     console.log(`selectedId=${displayJson(selectedId)}`);
  //     console.log(`outlineId=${displayJson(outlineId)}`);
  //     console.log(`state=${displayJson(state)}`);
  //   },
  //   [data, selectedId, outlineId, state],
  // );

  // useEffect(() => { console.log(`Table atBottom=${state.atBottom}`); }, [state.atBottom]);
  // useEffect(() => { console.log(`Table topId=${state.topId}`); }, [state.topId]);
  // useEffect(() => { console.log(`Table bottomId=${state.bottomId}`); }, [state.bottomId]);
  // useEffect(() => { console.log(`Table scrollToId=${state.scrollToId}`); }, [state.scrollToId]);

  useEffect(
    () => {
      // console.log('collapse all');
      dispatch({
        type: 'collapseAll',
        rowIdField,
        hierarchyCloseLevel,
        collapseAllTrigger,
      });
    },
    [collapseAllTrigger, rowIdField, hierarchyCloseLevel],
  );

  useEffect(
    () => {
      if (autoScrollToSelected) {
        const selCol = columns.find((thisCol) => (thisCol.id === rowIdField));

        if (!selCol) {
          logError(
            `Table: no column has the id field set to "${rowIdField}".`,
            'Table.autoScrollToSelected.missingColumn',
            {
              rowIdField,
              columnIds: columns?.map((c) => c.id),
            },
          );
          logError('The table will NOT be able to scroll to the selected row.');
        }
      }
    },
    [columns, rowIdField, autoScrollToSelected],
  );

  useEffect(
    () => {
      dispatch({
        type: 'latchAtTop',
        atTop: state.atTop,
      });
    },
    [state.atTop],
  );

  useEffect(
    () => {
      dispatch({
        type: 'latchAtBottom',
        atBottom: state.atBottom,
      });
    },
    [state.atBottom],
  );

  useEffect(
    () => {
      dispatch({
        type: 'latchNearTop',
        nearTop: state.nearTop,
      });
    },
    [state.nearTop],
  );

  useEffect(
    () => {
      dispatch({
        type: 'latchNearBottom',
        nearBottom: state.nearBottom,
      });
    },
    [state.nearBottom],
  );

  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns: columns.reduce(
        (prev, curr) => {
          if (curr.flex) return `${prev} ${curr.flex}fr`;
          return `${prev} auto`;
        },
        '',
      ),
    }),
    [columns],
  );

  const divClassName = useMemo(
    () => {
      // scrollbar-gutter:stable reserves space for the vertical scrollbar even when hidden.
      // Without it, Safari oscillates
      let ret = 'grid gap-0 justify-items-start items-center justify-start content-start border-collapse overflow-y-auto [scrollbar-gutter:stable] relative';

      if (className) ret += ` ${className}`;

      return ret;
    },
    [className],
  );

  useEffect(
    () => {
      if (state.nearTop && onScrollToTop && (!state.prevNearTop)) onScrollToTop();
    },
    [onScrollToTop, state.nearTop, state.prevNearTop],
  );

  useEffect(
    () => {
      if (state.nearBottom && onScrollToBottom && (!state.prevNearBottom)) onScrollToBottom();
    },
    [onScrollToBottom, state.nearBottom, state.prevNearBottom],
  );

  const handleScroll = useCallback(
    (event) => {
      const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
      dispatch({
        type: 'scroll',
        scrollHeight,
        scrollTop,
        clientHeight,
        edgeOffsetPx: scrollEdgeOffsetPx,
      });
    },
    [scrollEdgeOffsetPx],
  );

  useEffect(
    () => {
      if (state.scrollToId) {
        const msgElement = document.getElementById(state.scrollToId);
        // console.log(`scroll to "${state.scrollToId}"`);
        if (msgElement) {
          // console.log('found element');
          msgElement.scrollIntoView({
            behavior: 'smooth',
            block: state.scrollAlign,
          });
        }
        dispatch({ type: 'scrollDone' });
      }
    },
    [state.scrollToId, state.scrollAlign],
  );

  useEffect(
    () => {
      // console.log('Data changed, mark not ready');
      dispatch({ type: 'notReady' });
      // console.log('Data changed, compute');
      dispatch({
        type: 'dataChanged',
        label,
        data,
        rowIdField,
        preventScrollOnPrepend,
        preventScrollOnAppend,
        hierarchyIdField,
        hierarchyFlattened,
        hierarchyCloseLevel,
        selectedId,
        openOnlySelectedIdsOnInit,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      rowIdField, data, preventScrollOnPrepend, preventScrollOnAppend,
      hierarchyIdField, hierarchyFlattened, hierarchyCloseLevel, label,
      // Note: we intentionally don't add selectedId to dependencies
      // to avoid re-running on every selection change
    ],
  );

  useEffect(
    () => {
      dispatch({ type: 'notReady' });
      dispatch({
        type: 'outlineId',
        rowIdField,
        outlineId,
        openOnlyOutlinedIds,
      });
    },
    [
      rowIdField, data, // preventScrollOnPrepend, preventScrollOnAppend,
      hierarchyIdField, hierarchyFlattened, hierarchyCloseLevel, label,
      outlineId, openOnlyOutlinedIds,
    ],
  );

  const cellClassName = classNames(
    cellsClassName,
    'w-full h-full fill-black flex flex-row px-1 py-0.5 items-center relative',
    {
      'border-1 border-solid border-pxMainOutline': showBorders,
      'border-0': !showBorders,
    },
  );

  // Pre-compute className strings for each column to avoid classNames() calls in render loop
  const columnClassNames = useMemo(
    () => columns.map((col, idx) => {
      const base = classNames(
        cellClassName,
        { 'col-start-1': idx === 0 },
        col.rowCellClassName,
        col.columnCellClassName,
      );

      return {
        centerSelected: `${base} justify-center text-center ${selectedRowCellClassName || ''}`,
        centerNotSelected: `${base} justify-center text-center`,
        startSelected: `${base} justify-start text-start ${selectedRowCellClassName || ''}`,
        startNotSelected: `${base} justify-start text-start`,
        hasRenderRowCell: !!col.renderRowCell,
        forceCenter: col.center,
      };
    }),
    [columns, cellClassName, selectedRowCellClassName],
  );

  const header = useMemo(
    () => {
      if (!showHeader) return null;

      const ret = [];
      let someHeaderSet = false;

      columns.forEach((thisColumn) => {
        const {
          id, display, center, minWidth, columnCellClassName,
          coumnCellCustomBg,
        } = thisColumn;
        // Comment
        const key = `hdr_${id}`;

        const thisCellClassName = classNames(
          cellClassName,
          'z-20 font-bold sticky top-0',
          {
            'bg-white': !coumnCellCustomBg,
            'justify-center text-center': center,
            'justify-start text-start': !center,
          },
          columnCellClassName,
        );

        let thisStyle;
        if (minWidth) {
          let minWidthChars;
          if (typeof minWidth === 'string') minWidthChars = minWidth.length;
          else minWidthChars = minWidth;

          thisStyle = { minWidth: `${minWidthChars}ch` };
        }

        if (display) someHeaderSet = true;

        ret.push(
          <div
            className={thisCellClassName}
            key={key}
            style={thisStyle}
          >
            {(onClickColumn)
              ? (
                  <button
                    type="button"
                    className="w-full h-full opacity-0 absolute top-0 left-0"
                    id={id}
                    onClick={onClickColumn}
                    aria-label="selectCol"
                  />
                )
              : null}
            {display || ''}
          </div>,
        );
      });

      if (!someHeaderSet) return null;

      return ret;
    },
    [columns, onClickColumn, cellClassName, showHeader],
  );

  // Ref to allow handleHierarchyClick to check processing state without
  // depending on it (keeps the callback reference stable across toggles)
  const processingIdRef = useRef(state.processingHierarchyId);
  processingIdRef.current = state.processingHierarchyId;

  const handleHierarchyClick = useCallback(
    (event) => {
      // Ignore clicks while already processing (read from ref for stability)
      if (processingIdRef.current !== undefined) return;

      const clickedId = event.currentTarget.id;

      // Set processing state immediately (shows spinner, disables buttons)
      dispatch({
        type: 'setProcessingHierarchy',
        id: clickedId,
      });

      // Defer heavy computation to allow UI to update first
      setTimeout(
        () => {
          dispatch({
            type: 'clickHierarchyOpenClose',
            id: clickedId,
            rowIdField,
          });
        },
        0,
      );
    },
    [rowIdField],
  );

  // Memoized row click handler using event delegation pattern
  const handleRowClickInternal = useCallback(
    (event) => {
      if (!onClickRow) return;

      const { rowId } = event.currentTarget.dataset;
      const dataSel = state.hierarchyData || data;
      const thisRowData = dataSel.find(
        (row) => (row[rowIdField]?.toString() === rowId),
      );

      if (thisRowData) {
        const selectedIdArray = selectedId
          ? (Array.isArray(selectedId) ? selectedId : [selectedId])
          : [];
        const thisRowIsSelected = selectedIdArray.includes(thisRowData[rowIdField]);

        onClickRow(thisRowData, event, thisRowIsSelected);
      }
    },
    [onClickRow, state.hierarchyData, data, rowIdField, selectedId],
  );

  const lastScrolledIdRef = useRef();

  useLayoutEffect(
    () => {
      if (!selectedId
        || (Array.isArray(selectedId) && !selectedId.length)
        || !autoScrollToSelected
        || !data
        || !data.length) {
        // Reset so a re-selection of the same id will scroll again
        lastScrolledIdRef.current = undefined;
        return;
      }

      const firstSelectedId = Array.isArray(selectedId) ? selectedId[0] : selectedId;

      // Skip if we already scrolled to this exact selection
      if (lastScrolledIdRef.current === firstSelectedId) return;
      lastScrolledIdRef.current = firstSelectedId;

      const firstColumnId = (columns[0] || {}).id;
      const cellId = `cell_${firstColumnId}_${firstSelectedId}`;

      requestAnimationFrame(() => {
        const selectedCell = document.getElementById(cellId);
        if (selectedCell) {
          selectedCell.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      });
    },
    [autoScrollToSelected, columns, selectedId, data],
  );

  const rowsContent = useMemo(
    () => {
      if (!data) {
        return (
          <div className={classNames(
            'col-span-full text-center justify-self-stretch',
            {
              'border-1 border-solid border-pxMainOutline': showBorders,
              'border-0': !showBorders,
            },
          )}
          >
            ?
          </div>
        );
      }

      if (!data.length) {
        return (
          <div className={classNames(
            'col-span-full text-center justify-self-stretch',
            {
              'border-1 border-solid border-pxMainOutline': showBorders,
              'border-0': !showBorders,
            },
          )}
          >
            -
          </div>
        );
      }

      const dataSel = state.hierarchyData || data;

      let selectedIdArray;
      if (selectedId) {
        selectedIdArray = (Array.isArray(selectedId))
          ? selectedId
          : [selectedId];
      } else {
        selectedIdArray = [];
      }

      // console.log(`data=${displayJson(dataSel)}`);

      return dataSel
        .map((thisRowData, thisRowIndex) => {
          if (!thisRowData) {
            logError(
              `Table thisRowIndex=${thisRowIndex} thisRowData=${displayJson(thisRowData)}`,
              'Table.rowsContent.nullRowData',
              {
                thisRowIndex,
                dataLength: dataSel?.length,
              },
            );
            logError(`dataSel=${displayJson(dataSel)}`);
          }

          const thisRowId = thisRowData[rowIdField] || '';
          const thisRowIdString = thisRowId.toString();

          const isHidden = state.hierarchyData
            && state.hierarchyHidden.has(thisRowIdString);

          return (
            <TableRow
              key={`row_id_${thisRowId}`}
              rowData={thisRowData}
              rowIdField={rowIdField}
              rowIndex={thisRowIndex}
              columns={columns}
              columnClassNames={columnClassNames}
              cellClassName={cellClassName}
              hierarchyColumn={hierarchyColumn}
              handleRowClickInternal={handleRowClickInternal}
              handleHierarchyClick={handleHierarchyClick}
              isHidden={!!isHidden}
              isSelected={selectedIdArray.includes(thisRowId)}
              isClosed={thisRowData.th ? state.hierarchyClosed.has(thisRowIdString) : undefined}
              isProcessing={
                (state.processingHierarchyId !== undefined)
                && (state.processingHierarchyId === thisRowIdString)
              }
              nullDisplay={nullDisplay}
              undefinedDisplay={undefinedDisplay}
              renderFullWidthCell={renderFullWidthCell}
              hasOnClickRow={!!onClickRow}
            />
          );
        });
    },
    [
      columns, data, onClickRow, rowIdField, selectedId, renderFullWidthCell,
      nullDisplay, undefinedDisplay,
      state.hierarchyData, cellClassName, showBorders, hierarchyColumn,
      state.hierarchyHidden, state.hierarchyClosed, handleHierarchyClick,
      handleRowClickInternal, columnClassNames, state.processingHierarchyId,
    ],
  );

  return (
    <div
      className={divClassName}
      style={gridStyle}
      onScroll={handleScroll}
      id={id}
    >
      {header}
      {rowsContent}

      {(busy || (!state.ready) || isDataDeferred)
        ? (
            <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-white/50 z-30">
              <CircularProgress sizePx={32} />
            </div>
          )
        : null}
    </div>
  );
}

Table.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  // Used to indicate when Table is a new one:
  label: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    // id for this column
    id: PropTypes.string.isRequired,
    // header cell content
    display: PropTypes.node,
    // this column's cells classname
    columnCellClassName: PropTypes.string,
    // Set if this column's background is not white
    coumnCellCustomBg: PropTypes.bool,
    // data row cell classname
    rowCellClassName: PropTypes.string,
    // center content
    center: PropTypes.bool,
    // row cell render
    renderRowCell: PropTypes.func,
    // min. width for this column: in char's or from an example
    minWidth: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    // flex fraction for this column, auto if not set
    flex: PropTypes.number,
  })).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({
    // Data fields depend on the data being displayed,
    // and are not specified here
    //
    // Fields specific to this Table's display:
    tableRowFullWidth: PropTypes.bool, // set to have this data span the whole row
    tableOutline: PropTypes.bool, // set to have this data outlined, and its
    // parents to display the number of outlined children
  })),
  // Column header click:
  onClickColumn: PropTypes.func,
  // row click
  onClickRow: PropTypes.func,
  // unique id differentiating rows:
  rowIdField: PropTypes.string.isRequired,
  // classname to use for selected rows
  selectedRowCellClassName: PropTypes.string,
  // list of / single selected item(s)
  selectedId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
  autoScrollToSelected: PropTypes.bool,
  openOnlySelectedIdsOnInit: PropTypes.bool,
  // text to display for null fields
  nullDisplay: PropTypes.string,
  // text to display for undefined fields
  undefinedDisplay: PropTypes.string,
  onScrollToTop: PropTypes.func,
  onScrollToBottom: PropTypes.func,
  // Distance in px from an edge at which onScrollTo{Top,Bottom} pre-fire
  scrollEdgeOffsetPx: PropTypes.number,
  preventScrollOnAppend: PropTypes.bool,
  preventScrollOnPrepend: PropTypes.bool,
  hierarchyIdField: PropTypes.string,
  hierarchyFlattened: PropTypes.bool,
  hierarchyCloseLevel: PropTypes.number,
  renderFullWidthCell: PropTypes.func,
  cellsClassName: PropTypes.string,
  showHeader: PropTypes.bool,
  showBorders: PropTypes.bool,
  hierarchyColumn: PropTypes.number,
  collapseAllTrigger: PropTypes.bool,
  // for hierarchical display, items to mark as outlined:
  outlineId: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.number),
  ]),
  openOnlyOutlinedIds: PropTypes.bool,
  busy: PropTypes.bool,
};

export default Table;
